import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { Delete, Back } from '../../assets/images';
import InputField from '../formItem/inputField';
import Button from '../button';
import Popup from '../popup';
import { organizationAPI } from '../../actions/imsServer';
import { API_PAGE_SIZE } from '../../constants';

const StyledOrganizationItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    alignSelf: 'stretch',
    marginBottom: '10px',
}));

const OrganizationInfo = styled(Box)(({ theme }) => ({
    flex: 1,
    backgroundColor: theme.palette.figma.white[10],
    borderRadius: '15px',
    padding: '0 15px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const OrganizationName = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.figma.white[60],
    letterSpacing: '-0.42px',
}));

const OrganizationCode = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.figma.white[80],
    letterSpacing: '-0.42px',
}));

const HighlightText = styled('span')(({ theme }) => ({
    color: theme.palette.figma.Red_didim,
}));

const OrganizationSearchContent = ({
    onSelectOrganization,
    type = 'page',
    onClose
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [, setSelectedOrganization] = useState(null);

    // 팝업 상태
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');

    // 팝업 표시 함수 (reserved for future use)
    const _showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    // 팝업 닫기 함수
    const handleClosePopup = () => {
        setPopupOpen(false);
    };

    // 선택된 기관 제거 함수 (reserved for future use)
    const _handleRemoveSelectedOrganization = () => setSelectedOrganization(null);
    void _showPopup;
    void _handleRemoveSelectedOrganization;

    const handleSearch = async value => {
        setSearchTerm(value);
        setSearchError('');

        if (value.trim() === '') {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);

        try {
            const baseParams = {
                state: 0, // 상태 (0:정상 1:삭제 2:등록대기 3:블락. ',' 단위로 복수개 지원)
                keyword: value.trim(), // 키워드 (이름)
            };
            const fetchPage = (limit, offset) =>
                localStorage.getItem('accessToken')?.length > 0
                    ? organizationAPI.getOrganizations({ ...baseParams, limit, offset })
                    : organizationAPI.postOrganizations({ ...baseParams, limit, offset });

            const allOrganizations = [];
            const limit = API_PAGE_SIZE;
            let offset = 0;
            let hasMore = true;

            while (hasMore) {
                const response = await fetchPage(limit, offset);
                const list = response?.data?.organizations ?? response?.organizations ?? [];
                const arr = Array.isArray(list) ? list : [];
                allOrganizations.push(...arr);
                if (arr.length < limit) {
                    hasMore = false;
                } else {
                    offset += limit;
                }
            }

            setSearchResults(allOrganizations);
        } catch (error) {
            console.error(t('organization_search_error'), error);
            setSearchError(t('organization_search_error'));
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const highlightSearchTerm = (text, searchTerm) => {
        if (!searchTerm) return text;

        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <HighlightText key={index}>{part}</HighlightText>
            ) : (
                part
            )
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    width: '100%',
                }}
            >
                <IconButton
                    onClick={() => {
                        if (onClose) {
                            onClose();
                        } else {
                            navigate(-1);
                        }
                    }}
                    sx={{
                        '&:hover': {
                            backgroundColor: theme.palette.figma.white[20],
                        },
                    }}
                >
                    {type === 'page' ? <Back /> : <Delete />}
                </IconButton>
                <Box sx={{ flex: 1, height: '40px' }}>
                    <InputField
                        fullWidth
                        placeholder={t('organization_search_placeholder')}
                        value={searchTerm}
                        onChange={e => handleSearch(e.target.value)}
                        showClearIcon={true}
                        onClear={() => {
                            setSearchTerm('');
                            setSearchResults([]);
                        }}
                    />
                </Box>
            </Box >

            {/* Search Section */}
            < Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '40px',
                    flex: '1 0 0',
                    alignSelf: 'stretch',
                    padding: '40px 0',
                }}
            >
                {/* Search Results */}
                < Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '20px',
                        width: '100%',
                    }}
                >
                    {/* Helper Text */}
                    < Typography
                        sx={{
                            color: theme.palette.figma.white[60],
                            fontSize: '12px',
                            fontWeight: 400,
                            letterSpacing: '-0.36px',
                            textAlign: 'left',
                            alignSelf: 'flex-start',
                        }}
                    >
                        {t('organization_search_hint')}
                    </Typography >

                    {/* Results List */}
                    {
                        isSearching ? (
                            <Typography
                                sx={{
                                    color: theme.palette.figma.white[60],
                                    fontSize: '14px',
                                    textAlign: 'center',
                                }}
                            >
                                {t('searching')}
                            </Typography>
                        ) : searchError ? (
                            <Typography
                                sx={{
                                    color: theme.palette.figma.Red_didim,
                                    fontSize: '14px',
                                    textAlign: 'center',
                                }}
                            >
                                {searchError}
                            </Typography>
                        ) : searchResults.length > 0 ? (
                            <Box sx={{ width: '100%' }}>
                                {searchResults.map(organization => (
                                    <StyledOrganizationItem key={organization.id}>
                                        <OrganizationInfo>
                                            <OrganizationName>
                                                {highlightSearchTerm(
                                                    organization.name ||
                                                    organization.organizationName,
                                                    searchTerm
                                                )}
                                            </OrganizationName>
                                            <OrganizationCode>
                                                {organization.code ||
                                                    organization.organizationCode ||
                                                    organization.id}
                                            </OrganizationCode>
                                        </OrganizationInfo>
                                        <Button
                                            variant="normal"
                                            size="small"
                                            onClick={() => onSelectOrganization(organization)}
                                            sx={{
                                                minWidth: '60px',
                                                height: '40px',
                                            }}
                                        >
                                            {t('select')}
                                        </Button>
                                    </StyledOrganizationItem>
                                ))}
                            </Box>
                        ) : searchTerm ? (
                            <Typography
                                sx={{
                                    color: theme.palette.figma.white[60],
                                    fontSize: '14px',
                                    textAlign: 'center',
                                }}
                            >
                                {t('no_search_results')}
                            </Typography>
                        ) : null
                    }
                </Box >
            </Box >

            {/* 팝업 */}
            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />
        </Box>
    );
};

export default OrganizationSearchContent;
